import { useForm } from 'react-hook-form';
import { LoginForm, LoginFormSchema } from '../schemas/login_form';
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useUserAuthStore } from '../../../../stores/global/userAuth';
import { useDependencyInjection } from '../../../../config/di/dependency_injection_context';
import { EsqueceuSuaSenhaForm, EsqueceuSuaSenhaSchema } from '../schemas/esqueceu_sua_senha';
import { processError } from '../../../../common/utils/base_error';
import { TrocarSenhaForm, TrocarSenhaSchema } from '../schemas/trocar_senha';

/**
 * useLoginViewModel
 */

export const useLoginViewModel = () => {
  const {
    auth: {
      loginUseCase,
      esqueceuSuaSenhaUseCase,
      trocarSenhaUseCase,
    },
  } = useDependencyInjection();

  const navigate = useNavigate();

  const { setAuthInfos } = useUserAuthStore();

  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [infoMessage, setInfoMessage] = useState<string>();
  const [trocaSenhaInfoMessage, setTrocaSenhaInfoMessage] = useState<string>();

  const [esqueceuSuaSenhaModal, setEsqueceuSuaSenhaModal] = useState(false);
  const [trocarSenhaModal, setTrocarSenhaModal] = useState(false);

  const hookForm = useForm<LoginForm>({
    resolver: zodResolver(LoginFormSchema),
  });

  const esqueceuSuaSenhaHookForm = useForm<EsqueceuSuaSenhaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(EsqueceuSuaSenhaSchema),
  });

  const trocarSenhaHookForm = useForm<TrocarSenhaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(TrocarSenhaSchema),
  });

  const [login] = hookForm.watch(['login']);

  const submit = useCallback(async (form: LoginForm) => {
    try {
      const authInformation = await loginUseCase.login(form);
      setAuthInfos(authInformation);

      if (authInformation !== null) {
        if (authInformation.tableScheme !== null) {
          window.localStorage.setItem(`${authInformation.idUsuario}_columns`, authInformation.tableScheme);
        }
      }
    } catch (e: unknown) {
      if (typeof e === "string") {
        hookForm.setError("root", { message: e });
      } else if (e instanceof Error) {
        hookForm.setError("root", { message: e.message });
      }
    }
  }, [
    loginUseCase,
    hookForm,
    setAuthInfos,
  ]);

  const handleConfigure = useCallback(() => {
    navigate("/configure");
  }, [navigate]);

  const handleEsqueceuSuaSenhaModalOpenState = useCallback((open: boolean) => {
    setErrorMessage(undefined);
    setEsqueceuSuaSenhaModal(open);
  }, [
  ]);

  const handleEsqueceuSuaSenhaSubmit = useCallback(async (form: EsqueceuSuaSenhaForm) => {
    try {
      setSaving(true);

      setErrorMessage(undefined);
      setInfoMessage(undefined);

      await esqueceuSuaSenhaUseCase.esqueceuSuaSenha(form.email);

      setInfoMessage('Uma nova senha foi enviada para o seu email.');
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Não foi possível processar sua requisição.')[0]);
    } finally {
      setSaving(false);
    }
  }, [
    esqueceuSuaSenhaUseCase,
  ]);

  const handleTrocarSenhaOpenState = useCallback((openState: boolean) => {
    if (openState) {
      trocarSenhaHookForm.reset();
      if (login && login.trim().length > 0) {
        trocarSenhaHookForm.setValue("login", login);
      }
      setTrocaSenhaInfoMessage(undefined);
    }
    requestAnimationFrame(() => setTrocarSenhaModal(openState));
  }, [
    login,
    trocarSenhaHookForm,
  ]);

  const handleTrocarSenhaSubmit = useCallback(async (form: TrocarSenhaForm) => {
    try {
      setSaving(true);

      await trocarSenhaUseCase.trocarSenha(
        form.login,
        form.senhaAtual,
        form.novaSenha,
      );

      trocarSenhaHookForm.reset();

      setTrocaSenhaInfoMessage('Senha alterada com sucesso!');
    } catch (e: unknown) {
      const error = processError(e, 'Não foi possível alterar sua senha!')[0];
      trocarSenhaHookForm.setError("root", { message: error });
      setTrocaSenhaInfoMessage(undefined);
    } finally {
      setSaving(false);
    }
  }, [
    trocarSenhaUseCase,
    trocarSenhaHookForm,
  ]);

  return ({
    saving,
    errorMessage: errorMessage ?? null,
    infoMessage: infoMessage ?? null,
    trocaSenhaInfoMessage: trocaSenhaInfoMessage ?? null,
    esqueceuSuaSenhaModal,
    trocarSenhaModal,
    hookForm,
    esqueceuSuaSenhaHookForm,
    trocarSenhaHookForm,
    errors: hookForm.formState.errors,
    isSubmitting: hookForm.formState.isSubmitting,
    handleSubmit: hookForm.handleSubmit(submit),
    handleConfigure: handleConfigure,
    handleEsqueceuSuaSenhaModalOpenState,
    handleEsqueceuSuaSenhaSubmit,
    handleTrocarSenhaOpenState,
    handleTrocarSenhaSubmit,
  });
};